<template>
  <div class="artwork-create-step2">
    <h2>{{ $t('ARTWORK_UPLOAD.CATEGORY_HEADING')}}</h2>
    <v-form @submit.prevent="submit" autocomplete="off">
      <!-- Category and technique -->
      <v-layout row wrap py-2>
        <v-flex xs6>
          <v-select
            v-model="form.category_id"
            :error-messages="errors.category_id"
            :items="categories"
            :label="$t('COMMON.CATEGORY')"
            :loading="loading"
            menu-props="auto"
            item-text="name"
            item-value="id"
          />
        </v-flex>

        <v-flex xs6>
          <v-select
            v-model="form.technique_id"
            :error-messages="errors.technique_id"
            menu-props="auto"
            item-text="name"
            item-value="id"
            :items="techniques"
            :label="$t('COMMON.TECHNIQUE')"
          />
        </v-flex>
      </v-layout>

      <!-- Theme section -->
      <div class="my-3 pb-2">
        <div>
          <h4 class="d-inline-block mr-3">{{ $t('COMMON.THEME') }}</h4>
          <v-tooltip dark bottom max-width="300px">
            <template v-slot:activator="{ on }">
              <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
            </template>
            <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.THEME') }}</span>
          </v-tooltip>
        </div>
        <v-layout row wrap mt-2>
          <v-flex xs6 sm4 v-for="(theme, index) in themes" :key="index" pt-0>
            <v-checkbox
              :ripple="false"
              class="ma-0 pa-0"
              v-model="form.themes"
              color="primary"
              hide-details
              dense
              :label="theme.name"
              :value="theme.id"
            />
          </v-flex>
        </v-layout>
        <p v-for="e in errors.themes" :key="e" class="ma-0 pa-0 red--text">{{ e }}</p>
      </div>

      <!-- Aditional section -->
      <div v-if="!isCategorySculpture">
        <div>
          <h3 class="d-inline-block mr-3">{{$t('ARTWORK_UPLOAD.ADDITIONAL')}}</h3>
          <v-tooltip dark bottom max-width="300px">
            <template v-slot:activator="{ on }">
              <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
            </template>
            <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.ADDITIONAL') }}</span>
          </v-tooltip>
        </div>

        <v-layout row wrap align-center>
          <v-flex py-0>
            <v-radio-group
              v-model="form.is_framed"
              :error-messages="errors.is_framed"
              row
              class="py-0 my-2"
            >
              <v-radio :label="$t('ARTWORK_UPLOAD.FRAME')" :value="true" color="primary" />
              <v-radio :label="$t('ARTWORK_UPLOAD.NO_FRAME')" :value="false" color="primary" />
            </v-radio-group>
          </v-flex>
          <v-flex py-0>
            <v-checkbox
              :ripple="false"
              class="ma-0 pa-0"
              v-model="form.is_hang_ready"
              :error-messages="errors.is_hang_ready"
              :label="$t('ARTWORK_UPLOAD.HANG_READY')"
              color="primary"
            />
          </v-flex>
        </v-layout>
      </div>

      <!-- Description section -->
      <div class="mt-1">
        <h3>{{ $t('COMMON.DESCRIPTION') }}</h3>
        <!-- Year -->
        <v-select
          :items="years"
          v-model="form.created_year"
          :error-messages="errors.created_year"
          :label="$t('ARTWORK_UPLOAD.YEAR_CREATED')"
        />

        <!-- KEYWORDS -->
        <v-combobox
          :label="$t('ARTWORK_UPLOAD.KEYWORDS')"
          :hide-no-data="true"
          :error-messages="errors.keywords"
          :items="keywords"
          :hint="$t('ARTWORK_UPLOAD.TAGS_INFO')"
          :search-input.sync="keywordsSearch"
          :loading="keywordsLoading"
          :cache-items="true"
          v-model="form.keywords"
          hide-selected
          small-chips
          multiple
          deletable-chips
          persistent-hint
        >
          <template v-slot:append-outer>
            <v-tooltip dark bottom max-width="300px">
              <template v-slot:activator="{ on }">
                <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
              </template>
              <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.TAGS') }}</span>
            </v-tooltip>
          </template>
        </v-combobox>

        <!-- Languages tabs -->
        <tabs :form="form" :errors="errors" class="mt-3" />
      </div>

      <v-layout row wrap mt-4>
        <v-flex shrink>
          <tk-btn flat @click="$emit('prev')">{{ $t('COMMON.BACK') }}</tk-btn>
        </v-flex>

        <v-flex grow text-xs-right>
          <tk-btn :loading="saving" flat @click="save(true)">{{ $t('COMMON.SAVE_RETURN') }}</tk-btn>
          <tk-btn :loading="saving" color="primary" @click="save(false)">{{ $t('COMMON.CONTINUE') }}</tk-btn>
        </v-flex>
      </v-layout>

      <div class="text-xs-right"></div>
    </v-form>
  </div>
</template>

<script>
import { RepositoryFactory } from "@/services/RepositoryFactory";
import { ErrorHandler } from "@/utils/ErrorHandler";
import debounce from "@/utils/TinyDebounce";

const translationsRepo = RepositoryFactory.translations();
const artworksRepo = RepositoryFactory.artworks();
const searchRepo = RepositoryFactory.search();
const handler = new ErrorHandler();

export default {
  props: ["artwork"],

  components: {
    Tabs: () => import("./Tabs/Tabs"),
  },

  data() {
    return {
      keywordsSearch: null,
      categories: [],
      themes: [],
      keywords: [],
      loading: false,
      saving: false,
      keywordsLoading: false,
      errors: {
        title: {},
        description: {},
      },
      form: {
        themes: [],
        keywords: [],
        title: {
          fi: null,
          se: null,
          en: null,
        },
        description: {
          fi: null,
          se: null,
          en: null,
        },
      },
    };
  },

  created() {
    this.getTranslations();
    this.form = { ...this.form, ...this.artwork };
    console.log("STEP 2 created", this.form.is_hang_ready);
  },

  computed: {
    techniques() {
      if (this.form.category_id) {
        const pos = this.categories.findIndex(
          (cat) => cat.id === this.form.category_id
        );
        if (pos === -1) return;
        return this.categories[pos].techniques;
      }
      return [];
    },
    isCategorySculpture() {
      const cat = this.categories.find((c) => c.id === this.form.category_id);
      return cat && cat.slug === "sculpture";
    },
    years() {
      // Create years collection
      const values = [];
      const year = new Date().getFullYear();
      for (let i = 1970; i <= year; i++) {
        values.push(i);
      }
      return values;
    },
  },

  watch: {
    keywordsSearch: debounce(function (val) {
      this.fetchKeywords(val);
    }, 300),
  },

  methods: {
    async save(saveAndReturn) {
      this.saving = true;
      this.resetErrors();

      try {
        const res = await artworksRepo.createArtwork(
          this.artwork.id,
          2,
          saveAndReturn,
          this.form
        );

        if (saveAndReturn) {
          this.$router.push({ name: "profile.artworks" });
        } else {
          this.$emit("next", "step2");
        }
      } catch (error) {
        if (error.status === 422 && error.data) {
          this.errors = { ...this.errors, ...error.data.errors };
        }

        this.$emit("error", "step2");
      }

      this.saving = false;
    },

    async getTranslations() {
      this.loading = true;

      await handler.handle(async () => {
        const res = await translationsRepo.getTranslations("categories,themes");
        this.categories = res.categories;
        this.themes = res.themes;
      });

      this.loading = false;
    },

    async fetchKeywords(query) {
      this.keywordsLoading = true;

      await handler.handle(async () => {
        this.keywords = await searchRepo.keywords(query);
      });

      this.keywordsLoading = false;
    },

    resetErrors() {
      this.errors = {
        title: {},
        description: {},
      };
    },
  },
};
</script>

<style lang="scss">
.artwork-create-step2 {
  .v-tabs__div.has-error {
    color: #ff5252 !important;
    a {
      color: #ff5252 !important;
    }
  }
}
</style>
